import axios from 'axios';
import {
  ASSIGN_USER_TO_PROJECT,
  CREATE_USER,
  GET_USERS,
  get_request_config
} from '@/utils/system_constants/constant.js';
import { normalize_project_id } from './tenant_settings_project_helpers';

export function normalize_users_response(response_data = {}) {
  return Array.isArray(response_data.data) ? response_data.data : [];
}

export function contact_email(component) {
  return String(component.tenant_setup_form.primary_email || '').trim().toLowerCase();
}

export function profile_email(profile = {}) {
  const user = profile.user || profile;
  return String(user.email || profile.email || '').trim().toLowerCase();
}

export function contact_user_id(contact = {}) {
  const user = contact?.user || contact;
  return normalize_project_id(contact?.user_id || user?.id || contact?.id);
}

export async function fetch_tenant_setup_users(component) {
  const response = await axios.get(
    GET_USERS,
    get_request_config({ params: { page: 1, page_size: 100 } })
  );
  return normalize_users_response(component.parse_api_data(response.data));
}

export async function tenant_setup_contact_users(component) {
  if (!component.tenant_project_id && component.people_records.length) {
    return component.people_records;
  }

  return fetch_tenant_setup_users(component);
}

export async function find_tenant_setup_contact(component) {
  const users = await tenant_setup_contact_users(component);
  const target_email = contact_email(component);

  return users.find((profile) => profile_email(profile) === target_email) || null;
}

export function build_contact_payload(component) {
  const contact_name = component.tenant_setup_contact_name();

  return {
    first_name: contact_name.first_name,
    last_name: contact_name.last_name,
    email: String(component.tenant_setup_form.primary_email).trim(),
    phone_number: String(component.tenant_setup_form.primary_phone).replace(/\D/g, ''),
    role: 'System Admin'
  };
}

export async function create_tenant_setup_contact(component) {
  const response = await axios.post(
    CREATE_USER,
    build_contact_payload(component),
    get_request_config()
  );
  const response_data = component.parse_api_data(response.data);
  const user_id = normalize_project_id(response_data?.data?.id || response_data?.user_id);

  if (!user_id) {
    throw new Error(response_data.message || 'Primary contact could not be created.');
  }

  return user_id;
}

export async function ensure_contact_user_id(component) {
  if (component.tenant_setup_contact_user_id) {
    return component.tenant_setup_contact_user_id;
  }

  const existing_contact = await component.find_tenant_setup_contact();
  const user_id = contact_user_id(existing_contact)
    || await component.create_tenant_setup_contact();
  component.tenant_setup_contact_user_id = user_id;
  return user_id;
}

export async function assigned_tenant_users(component) {
  const response = await axios.get(
    GET_USERS,
    get_request_config({
      params: {
        project_id: component.tenant_setup_project_id,
        page: 1,
        page_size: 100
      }
    })
  );

  return normalize_users_response(component.parse_api_data(response.data));
}

export function is_contact_assigned(users = [], user_id) {
  return users.some((profile) => contact_user_id(profile) === user_id);
}

export function resolve_assigner_id(component) {
  return normalize_project_id(
    component.$store?.getters?.currentUserId ||
    sessionStorage.getItem('current_user_id') ||
    sessionStorage.getItem('user_id')
  );
}

export async function assign_contact_to_project(component, assigner_id) {
  const response = await axios.post(
    ASSIGN_USER_TO_PROJECT,
    {
      project: component.tenant_setup_project_id,
      assigner: assigner_id,
      assignee: component.tenant_setup_contact_user_id
    },
    get_request_config()
  );
  const response_data = component.parse_api_data(response.data);

  if (response_data?.status && response_data.status !== 'success') {
    throw new Error(response_data.message || 'Primary contact could not be assigned.');
  }
}

export async function ensure_tenant_setup_contact(component) {
  try {
    const user_id = await ensure_contact_user_id(component);
    if (component.tenant_setup_contact_assigned) {
      return;
    }

    const assigned_users = await assigned_tenant_users(component);
    if (is_contact_assigned(assigned_users, user_id)) {
      component.tenant_setup_contact_assigned = true;
      return;
    }

    const assigner_id = resolve_assigner_id(component);
    if (!assigner_id) {
      throw new Error('Current user could not be resolved for project assignment.');
    }

    await assign_contact_to_project(component, assigner_id);
    component.tenant_setup_contact_assigned = true;
  } catch (error) {
    throw normalize_contact_error(error);
  }
}

function normalize_contact_error(error) {
  return error instanceof Error
    ? error
    : new Error('Primary contact could not be assigned.');
}
